import React, { useCallback, useEffect, useState } from 'react';
import { clientsAPI, trailersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import additionIcon from '../assets/icons/addition.png';
import './TrailerList.css';

interface Trailer {
  id: number;
  registrationNumber: string;
  makeAndModel: string;
  chassisNumber: string;
  declaredValue: number;
}

interface TrailerForm {
  registrationNumber: string;
  makeAndModel: string;
  chassisNumber: string;
  declaredValue: string;
}

const EMPTY_FORM: TrailerForm = {
  registrationNumber: '',
  makeAndModel: '',
  chassisNumber: '',
  declaredValue: '',
};

const VALUE_ONES = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
  'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
  'seventeen', 'eighteen', 'nineteen'];
const VALUE_TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

const numberToWords = (value: number): string => {
  if (value === 0) return 'zero';
  if (value >= 1_000_000) {
    const millions = Math.floor(value / 1_000_000);
    const remainder = value % 1_000_000;
    return `${numberToWords(millions)} million${remainder ? ` ${numberToWords(remainder)}` : ''}`;
  }
  if (value >= 1_000) {
    const thousands = Math.floor(value / 1_000);
    const remainder = value % 1_000;
    return `${numberToWords(thousands)} thousand${remainder ? ` ${numberToWords(remainder)}` : ''}`;
  }
  if (value >= 100) {
    const hundreds = Math.floor(value / 100);
    const remainder = value % 100;
    return `${VALUE_ONES[hundreds]} hundred${remainder ? ` and ${numberToWords(remainder)}` : ''}`;
  }
  if (value >= 20) {
    const tens = Math.floor(value / 10);
    const remainder = value % 10;
    return `${VALUE_TENS[tens]}${remainder ? `-${VALUE_ONES[remainder]}` : ''}`;
  }
  return VALUE_ONES[value];
};

const declaredValuePreview = (rawValue: string) => {
  const value = Number.parseInt(rawValue.replace(/[^0-9]/g, ''), 10);
  if (!rawValue || Number.isNaN(value) || value <= 0) return null;
  const words = numberToWords(value);
  return {
    monetary: `£${value.toLocaleString('en-GB')}.00`,
    words: `${words.charAt(0).toUpperCase()}${words.slice(1)} pounds`,
  };
};

const TrailerList: React.FC = () => {
  const { user } = useAuth();
  const clientId = user?.clientId;
  const [trailers, setTrailers] = useState<Trailer[]>([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<TrailerForm>(EMPTY_FORM);
  const [declaredValueFocused, setDeclaredValueFocused] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTrailers = useCallback(async () => {
    if (!clientId) {
      setLoading(false);
      return;
    }
    try {
      const client = await clientsAPI.getById(clientId);
      if (client.data.trailersEnabled !== true) {
        setEnabled(false);
        setTrailers([]);
        return;
      }
      setEnabled(true);
      const response = await trailersAPI.getAll(clientId);
      setTrailers(response.data);
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Unable to load the trailer register.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadTrailers();
  }, [loadTrailers]);

  const resetForm = () => {
    setShowForm(false);
    setForm(EMPTY_FORM);
    setDeclaredValueFocused(false);
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!clientId) return;
    setSaving(true);
    setError(null);
    try {
      await trailersAPI.create({
        clientId,
        registrationNumber: form.registrationNumber,
        makeAndModel: form.makeAndModel,
        chassisNumber: form.chassisNumber,
        declaredValue: Number(form.declaredValue),
      });
      resetForm();
      await loadTrailers();
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Failed to add the trailer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (trailer: Trailer) => {
    if (!window.confirm(`Remove trailer ${trailer.registrationNumber} from the register?`)) return;
    try {
      await trailersAPI.delete(trailer.id);
      setTrailers((current) => current.filter((item) => item.id !== trailer.id));
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Failed to remove the trailer. Please try again.');
    }
  };

  if (loading) return <div>Loading trailers...</div>;
  if (!enabled) {
    return (
      <div className="trailer-list">
        <h1>Trailers</h1>
        <div className="trailer-unavailable">Trailer cover has not been enabled for this client. Please contact your administrator.</div>
      </div>
    );
  }

  return (
    <div className="trailer-list">
      <div className="page-header">
        <h1>Trailers</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          <img src={additionIcon} alt="" className="trailer-add-icon" />
          Add Trailer
        </button>
      </div>

      <section className="trailer-policy-panel" aria-label="Trailer cover information">
        <p>
          This section is for trailers that require cover under your motor fleet policy. Cover may apply to trailers while attached to or temporarily detached from your vehicle, subject to the terms of your policy. Trailers must be owned by you, hired to you under a hire purchase agreement, or in your custody or under your control. Cover does not extend to goods carried in or on the trailer. All trailers must be towed in accordance with the law.
        </p>
        <p>For full details of the inclusions, exclusions and conditions applicable to trailer cover, please refer to your policy wording.</p>
        <p className="trailer-policy-contact">
          At Yourshield, we are here to help. If you are unsure whether a trailer is already covered, whether it needs to be declared, or what cover applies, please call us on <a href="tel:03333239660">0333 323 9660</a>.
        </p>
      </section>

      {error && <div className="trailer-error">{error}</div>}
      {trailers.length === 0 ? (
        <div className="trailer-empty-state">No trailers have been added for this client.</div>
      ) : (
        <div className="trailer-table-wrap">
          <table className="trailer-table">
            <thead>
              <tr>
                <th>Trailer Registration Number</th>
                <th>Trailer Make and Model</th>
                <th>Trailer Chassis Number</th>
                <th>Declared Value</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {trailers.map((trailer) => (
                <tr key={trailer.id}>
                  <td><strong>{trailer.registrationNumber || '—'}</strong></td>
                  <td>{trailer.makeAndModel}</td>
                  <td>{trailer.chassisNumber || '—'}</td>
                  <td>£{Number(trailer.declaredValue).toLocaleString('en-GB')}.00</td>
                  <td><button className="btn btn-danger btn-sm" onClick={() => handleRemove(trailer)}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content trailer-form-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Trailer</h2>
              <button className="close-btn" onClick={resetForm} aria-label="Close">✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Trailer Registration Number</label>
                <input maxLength={50} value={form.registrationNumber} onChange={(event) => setForm({ ...form, registrationNumber: event.target.value.toUpperCase() })} placeholder="e.g. C123456 (if available)" />
              </div>
              <div className="form-group">
                <label>Trailer Make and Model *</label>
                <input required maxLength={200} value={form.makeAndModel} onChange={(event) => setForm({ ...form, makeAndModel: event.target.value })} placeholder="e.g. Ifor Williams LM166" />
              </div>
              <div className="form-group">
                <label>Trailer Chassis Number</label>
                <input maxLength={100} value={form.chassisNumber} onChange={(event) => setForm({ ...form, chassisNumber: event.target.value.toUpperCase() })} placeholder="Enter chassis number (if available)" />
              </div>
              <div className="form-group">
                <label>Declared Value (£) *</label>
                <input
                  required
                  inputMode="numeric"
                  value={form.declaredValue ? (declaredValueFocused ? form.declaredValue : declaredValuePreview(form.declaredValue)?.monetary) : ''}
                  onFocus={() => setDeclaredValueFocused(true)}
                  onBlur={() => setDeclaredValueFocused(false)}
                  onChange={(event) => setForm({ ...form, declaredValue: event.target.value.replace(/[^0-9]/g, '') })}
                  placeholder="e.g. £15,000.00"
                />
                {(() => {
                  const preview = declaredValuePreview(form.declaredValue);
                  return preview ? <div className="trailer-value-preview">{preview.monetary} — {preview.words}</div> : null;
                })()}
                <details className="trailer-declared-value-help">
                  <summary>Help with valuing the trailer?</summary>
                  <p>The Declared Value should reflect the trailer's <strong>current market value</strong>, taking into account:</p>
                  <ul>
                    <li><strong>Age</strong> — older trailers may have depreciated significantly in value</li>
                    <li><strong>Condition</strong> — fair assessment of body, chassis and mechanical condition</li>
                    <li><strong>Modifications</strong> — non-standard changes may affect value</li>
                  </ul>
                </details>
                <small>Declared Value should reflect the trailer's current market value, taking into account its age, condition and any modifications. In the event of a total loss, the insurer uses market value at the time of the incident.</small>
                <small className="trailer-value-warning">If the declared value is materially incorrect, cover may be invalidated.</small>
              </div>
              {error && <div className="trailer-error">{error}</div>}
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Adding…' : 'Add Trailer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrailerList;
